<template>
  <button
    v-if="!isMobile"
    v-tooltip="{
      content: content,
      triggers: ['click', 'touch', 'hover'],
      autoHide: true,
      placement: placement,
      html: true,
      noAutoFocus: true,
    }"
    type="button"
  >
    <slot></slot>
  </button>

  <VDropdown
    v-if="isMobile"
    :positioning-disabled="isMobile"
    @apply-show="isMobile && onShow()"
    @apply-hide="isMobile && onHide()"
  >
    <slot></slot>

    <template #popper="{ hide }">
      <p class="mb-3 text-sm font-normal text-grey-800">{{ content }}</p>
      <button
        v-if="isMobile"
        type="button"
        class="flex items-center gap-1 text-sm text-link"
        @click="hide()"
      >
        Dismiss <IconClose class="size-[18px] fill-link" />
      </button>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
interface Props {
  content: string;
  placement?:
    | "auto"
    | "auto-start"
    | "auto-end"
    | "top"
    | "top-start"
    | "top-end"
    | "right"
    | "right-start"
    | "right-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end";
}

const props = withDefaults(defineProps<Props>(), {
  placement: "top",
});

const count = ref(0);
const isMobile = ref(false);

onMounted(() => {
  const handleResize = () => {
    isMobile.value = window.innerWidth < 640;
  };

  handleResize();
  window.addEventListener("resize", handleResize);
  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });
});

const onShow = () => {
  if (count.value === 0) {
    document.body.classList.add("no-scroll");
  }
  count.value++;
};

const onHide = () => {
  count.value--;
  if (count.value === 0) {
    document.body.classList.remove("no-scroll");
  }
};
</script>

<style>
body.no-scroll {
  @apply overflow-hidden;
}

.v-popper--theme-tooltip .v-popper__inner {
  @apply max-w-[200px] border border-grey-900/10 bg-sky-100 text-xs font-medium text-grey-700;
}

.v-popper--theme-tooltip .v-popper__arrow-outer {
  @apply border-grey-900/20;
}

.v-popper--theme-tooltip .v-popper__arrow-inner {
  @apply visible border-sky-100;
}

.v-popper--theme-dropdown .v-popper__inner {
  @apply m-4 rounded-xl border border-grey-900/10 bg-sky-50 px-4 py-3 shadow-sm;
}

.v-popper__popper--no-positioning {
  @apply fixed left-0 top-0 z-[9999] flex h-full w-full items-end;
}

.v-popper__popper--no-positioning .v-popper__backdrop {
  @apply block bg-grey-900/25;
}

.v-popper__popper--no-positioning .v-popper__wrapper {
  @apply w-full transform cursor-auto transition-transform duration-150 ease-out;
}

.v-popper__popper--no-positioning.v-popper__popper--hidden .v-popper__wrapper {
  @apply translate-y-full;
}
</style>
